import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/react-hooks';
import dayjs from 'dayjs';
import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import type { RootState } from '../../store/store';
import { name as reportFacetSelector, setPage } from '../../store/reducers/reportFacet';
import { reportGames } from './apiQueries';
import { Game, StringFilterInput } from '../../utils/types';
import ReportList from './ReportList';
import ReportFilters from './ReportFilters';

const PAGE_SIZE = 30;
const SORT_DIRECTION = 'DESC';
const AUTO_REFETCH_PERIOD = 30 * 1000; // 30 seconds in milliseconds
const LIMIT = 500;

const Report: FC = () => {
	const dispatch = useDispatch();

	// All these search facets come from the reportFacetSelector in the redux store
	const { faceted, sport, startTime, endTime, hasAirings, status, league, page, count, autoRefetch } = useSelector(
		(storedState: RootState) => storedState[reportFacetSelector]
	);

	// Set up query filters
	const filters: ReportFiltersInput = {};
	if (sport) filters.sport = { beginsWith: sport };

	const calcEndTime =
		startTime && endTime && dayjs(startTime).isBefore(dayjs(endTime).add(1, 'day')) ? dayjs(endTime).add(1, 'day').format('YYYY-MM-DD') : '';

	// The main query hook
	const { loading, error, data, refetch, startPolling, stopPolling } = useQuery(reportGames, {
		variables: {
			startTime,
			endTime: calcEndTime,
			nextToken: null,
			limit: LIMIT,
			sortDirection: SORT_DIRECTION,
			filter: Object.keys(filters).length > 0 ? filters : null
		},
		pollInterval: autoRefetch ? AUTO_REFETCH_PERIOD : 0
	});

	// Configure autoRefetch on the useQuery hook
	useEffect(() => {
		if (autoRefetch) {
			startPolling(AUTO_REFETCH_PERIOD);
		} else {
			stopPolling();
		}
	}, [startPolling, stopPolling, autoRefetch]);

	// Requery when filters update
	useEffect(() => {
		// To prevent false triggering we have to compose the filters again, inside useEffect
		const ueFilters: ReportFiltersInput = {};
		if (sport) ueFilters.sport = { beginsWith: sport };

		refetch({
			startTime,
			endTime: calcEndTime,
			nextToken: null,
			sortDirection: SORT_DIRECTION,
			limit: LIMIT,
			filter: Object.keys(ueFilters).length > 0 ? ueFilters : null
		});
	}, [startTime, calcEndTime, refetch, sport]);

	const nextToken = data?.listGames.nextToken || null;

	const handlePageChange = (newPage: number): void => {
		dispatch(setPage(newPage));
	};

	const renderGameList = (): JSX.Element => {
		if (loading) return <Typography>Loading...</Typography>;
		if (error) return <Typography>Error loading data from the server</Typography>;

		const filteredGames = data?.listGames.items.filter((game: Game) => {
			if (hasAirings && game.airings && game.airings?.items.length === 0) return false;
			if (status && game.status !== status) return false;
			if (league && game.league?.name !== league) return false;
			return true;
		});

		if (!filteredGames || (filteredGames.length === 0 && !nextToken)) return <Typography>No games found</Typography>;

		filteredGames.sort((a: Game, b: Game) => {
			if (!a.startTime) return -1;
			if (!b.startTime) return 1;
			return dayjs(a!.startTime).isBefore(dayjs(b!.startTime)) ? 1 : -1;
		});

		const currentPage = filteredGames.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

		return (
			<ReportList
				pageSize={PAGE_SIZE}
				page={page}
				games={currentPage}
				count={count || filteredGames.length}
				onPageChange={handlePageChange}
				refetch={refetch}
			/>
		);
	};

	return (
		<>
			<ReportFilters />
			{!loading && nextToken && faceted && (
				<Alert severity='warning' style={{ marginTop: -10, marginBottom: 10 }}>
					Please consider narrowing your search, which is slow and too broad for accurate paginated display.
				</Alert>
			)}
			{renderGameList()}
		</>
	);
};

type ReportFiltersInput = {
	sport?: StringFilterInput | null;
};

export default Report;
